import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { PageMeta } from '@/components/seo/page-meta'
import { PELGO_PRIVACY_SEO } from '@/lib/pelgo-seo'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

export function PrivacyPolicyPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PageMeta config={PELGO_PRIVACY_SEO} />
      <MarketingNav variant="standalone" />

      <div className="container max-w-4xl py-12 px-4 pt-32">
        {/* Header */}
        <div className="mb-12 scroll-fade-up in-view">
          <h1 className="text-4xl font-medium text-slate-900 mb-4">PRIVACY NOTICE</h1>
          <p className="text-sm text-slate-600">
            <strong>Last updated</strong> February 24, 2026
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-10 scroll-fade-up">
          {/* Intro */}
          <section>
            <p className="text-slate-700 leading-relaxed">
              This Privacy Notice for Pelgo, Inc. (&quot;Pelgo,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;), describes how and why we might access, collect,
              store, use, and/or share (&quot;process&quot;) your personal information when you use
              our services to engage with us, including any marketing or events
              (&quot;Services&quot;), including when you visit our website at{' '}
              <a
                href="https://www.pelgo.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://www.pelgo.com
              </a>{' '}
              or any website of ours that links to this Privacy Notice (collectively, the
              &quot;Platform&quot;).
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              &quot;You&quot;, &quot;you&quot; or &quot;your&quot; refers to users of our Platform
              and other persons who provide personal information to us. If you are an individual,
              this Privacy Notice also provides you with important information about your personal
              information rights with respect to personal information that we control and how to
              exercise them.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              This Privacy Notice does not apply to personal information that we collect from
              employees of or job applicants to Pelgo in connection with their employment or
              potential employment with us. This Privacy Notice covers how we treat personal
              information that we collect from you, or in connection with our Platform provided to
              you, or in connection with our other business or interactions with you. This Privacy
              Notice does not cover, and we are not responsible for, the policies or practices of
              yours or third parties that you may access or be connected with through us, which have
              their own rules for how they collect and use personal information. Please make any
              privacy requests directly to third parties with respect to personal information in
              their control.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Our Platform may include links to third-party websites, plug-ins, and applications.
              Clicking on third-party links or enabling third-party connections may allow third
              parties to collect or share personal information. We do not endorse or control these
              third-party websites, plug-ins, and applications and are not responsible for their
              privacy practices or any information on their websites. When you leave our Platform,
              we encourage you to read the privacy policy of every website you visit.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              By accessing or using our Platform, or by providing personal information to us, you
              acknowledge that you accept our privacy practices and policies outlined herein, and
              you consent that we may collect, process, store and share personal information as
              described in this Privacy Notice.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Questions or concerns? Reading this Privacy Notice will help you understand your
              privacy rights and choices. We are responsible for making decisions about how your
              personal information is processed. If you do not agree with our policies and
              practices, please do not use our Platform. If you still have any questions or
              concerns, please contact us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              .
            </p>
          </section>

          {/* Summary of Key Points */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Summary of Key Points</h2>
            <p className="text-slate-700 leading-relaxed">
              This summary provides key points from our Privacy Notice, but you can find out more
              details about any of these topics by using our table of contents below to find the
              section you are looking for.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Definition of Personal Information
            </h3>
            <p className="text-slate-700 leading-relaxed">
              We collect, process, store and share information that identifies, relates to or could
              reasonably be linked, directly or indirectly, with a particular individual or
              household (&quot;personal information&quot;). Personal information does not include
              information publicly available from government records, or which is not personal, like
              anonymous, deidentified, or aggregated data (even if it originally comes from personal
              information).
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>
                <strong>What personal information do we process?</strong> When you visit, use, or
                navigate our Platform, we may process personal information depending on how you
                interact with us and the Platform, the choices you make, and the products and
                features you use. Learn more in{' '}
                <a href="#infocollect" className="text-blue-600 hover:underline">
                  Section 1
                </a>
                .
              </li>
              <li>
                <strong>Do we process any sensitive personal information?</strong> We do not process
                sensitive personal information.
              </li>
              <li>
                <strong>Do we collect any information from third parties?</strong> We may collect
                information from public databases, marketing partners, social media platforms, and
                other outside sources. Learn more in{' '}
                <a href="#othersources" className="text-blue-600 hover:underline">
                  Section 1
                </a>
                .
              </li>
              <li>
                <strong>How do we process your information?</strong> We process your information to
                provide, improve, and administer our Services, communicate with you, for security
                and fraud prevention, and to comply with law. Learn more in{' '}
                <a href="#infouse" className="text-blue-600 hover:underline">
                  Section 2
                </a>
                .
              </li>
              <li>
                <strong>When and with whom do we share personal information?</strong> We may share
                information in specific situations and with specific categories of third parties.
                Learn more in{' '}
                <a href="#whoshare" className="text-blue-600 hover:underline">
                  Section 4
                </a>
                .
              </li>
              <li>
                <strong>How do we keep your information safe?</strong> We have adequate
                organizational and technical processes and procedures in place to protect your
                personal information. Learn more in{' '}
                <a href="#infosafe" className="text-blue-600 hover:underline">
                  Section 8
                </a>
                .
              </li>
              <li>
                <strong>What are your rights?</strong> Depending on where you are located, you may
                have certain rights regarding your personal information. Learn more in{' '}
                <a href="#privacyrights" className="text-blue-600 hover:underline">
                  Section 10
                </a>
                .
              </li>
              <li>
                <strong>How do you exercise your rights?</strong> The easiest way is by visiting{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline">
                  this page
                </Link>{' '}
                or by contacting us.
              </li>
            </ul>
          </section>

          {/* Table of Contents */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Table of Contents</h2>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>
                <a href="#infocollect" className="text-blue-600 hover:underline">
                  1. What information do we collect?
                </a>
              </li>
              <li>
                <a href="#infouse" className="text-blue-600 hover:underline">
                  2. How do we process your information?
                </a>
              </li>
              <li>
                <a href="#legalbases" className="text-blue-600 hover:underline">
                  3. What legal bases do we rely on to process your personal information?
                </a>
              </li>
              <li>
                <a href="#whoshare" className="text-blue-600 hover:underline">
                  4. When and with whom do we share your personal information?
                </a>
              </li>
              <li>
                <a href="#cookies" className="text-blue-600 hover:underline">
                  5. Do we use cookies and other tracking technologies?
                </a>
              </li>
              <li>
                <a href="#ai" className="text-blue-600 hover:underline">
                  6. Do we offer artificial intelligence-based products?
                </a>
              </li>
              <li>
                <a href="#inforetain" className="text-blue-600 hover:underline">
                  7. How long do we keep your information?
                </a>
              </li>
              <li>
                <a href="#infosafe" className="text-blue-600 hover:underline">
                  8. How do we keep your information safe?
                </a>
              </li>
              <li>
                <a href="#infominors" className="text-blue-600 hover:underline">
                  9. Do we collect information from minors?
                </a>
              </li>
              <li>
                <a href="#privacyrights" className="text-blue-600 hover:underline">
                  10. What are your privacy rights?
                </a>
              </li>
              <li>
                <a href="#DNT" className="text-blue-600 hover:underline">
                  11. Controls for do-not-track features
                </a>
              </li>
              <li>
                <a href="#uslaws" className="text-blue-600 hover:underline">
                  12. Do United States residents have specific privacy rights?
                </a>
              </li>
              <li>
                <a href="#policyupdates" className="text-blue-600 hover:underline">
                  13. Do we make updates to this notice?
                </a>
              </li>
              <li>
                <a href="#contact" className="text-blue-600 hover:underline">
                  14. How can you contact us about this notice?
                </a>
              </li>
              <li>
                <a href="#request" className="text-blue-600 hover:underline">
                  15. How can you review, update, or delete the data we collect from you?
                </a>
              </li>
              <li>
                <a href="#extension" className="text-blue-600 hover:underline">
                  Browser Extension
                </a>
              </li>
            </ul>
          </section>

          {/* 1. What Information Do We Collect? */}
          <section id="infocollect">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              1. What information do we collect?
            </h2>

            <h3 id="personalinfo" className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Personal information you disclose to us
            </h3>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> We collect personal information that you provide to us.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We collect personal information that you voluntarily provide to us when you register
              on the Platform, express an interest in obtaining information about us or our products
              and Platform, when you participate in activities on the Platform, or otherwise when
              you contact us.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Personal Information Provided by You.</strong> The personal information that
              we collect depends on the context of your interactions with us and the Platform, the
              choices you make, and the products and features you use. The personal information we
              collect may include the following:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>names</li>
              <li>phone numbers</li>
              <li>email addresses</li>
              <li>job titles</li>
              <li>usernames</li>
              <li>passwords</li>
              <li>contact preferences</li>
              <li>contact or authentication data</li>
              <li>mailing addresses</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Sensitive Information.</strong> We do not process sensitive information.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              All personal information that you provide to us must be true, complete, and accurate,
              and you must notify us of any changes to such personal information.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Information automatically collected
            </h3>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> Some information — such as your Internet Protocol (IP)
                address and/or browser and device characteristics — is collected automatically when
                you visit our Platform.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We automatically collect certain information when you visit, use, or navigate the
              Platform. This information does not reveal your specific identity (like your name or
              contact information) but may include device and usage information, such as your IP
              address, browser and device characteristics, operating system, language preferences,
              referring URLs, device name, country, location, information about how and when you use
              our Platform, and other technical information. This information is primarily needed to
              maintain the security and operation of our Platform, and for our internal analytics
              and reporting purposes.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Like many businesses, we also collect information through cookies and similar
              technologies.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              The information we collect includes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>
                <em>Log and Usage Data.</em> Log and usage data is service-related, diagnostic,
                usage, and performance information our servers automatically collect when you access
                or use our Platform and which we record in log files. Depending on how you interact
                with us, this log data may include your IP address, device information, browser
                type, and settings and information about your activity in the Platform (such as the
                date/time stamps associated with your usage, pages and files viewed, searches, and
                other actions you take such as which features you use), device event information
                (such as system activity, error reports (sometimes called "crash dumps"), and
                hardware settings).
              </li>
              <li>
                <em>Device Data.</em> We collect device data such as information about your
                computer, phone, tablet, or other device you use to access the Platform. Depending
                on the device used, this device data may include information such as your IP address
                (or proxy server), device and application identification numbers, location, browser
                type, hardware model, Internet service provider and/or mobile carrier, operating
                system, and system configuration information.
              </li>
              <li>
                <em>Location Data.</em> We collect location data such as information about your
                device's location, which can be either precise or imprecise. How much information we
                collect depends on the type and settings of the device you use to access the
                Platform. For example, we may use GPS and other technologies to collect geolocation
                data that tells us your current location (based on your IP address). You can opt out
                of allowing us to collect this information either by refusing access to the
                information or by disabling your Location setting on your device. However, if you
                choose to opt out, you may not be able to use certain aspects of the Platform.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Google API</h3>
            <p className="text-slate-700 leading-relaxed">
              Our use of information received from Google APIs will adhere to{' '}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Google API Services User Data Policy
              </a>
              , including the{' '}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Limited Use requirements
              </a>
              .
            </p>

            <h3 id="othersources" className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Information collected from other sources
            </h3>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> We may collect limited data from public databases,
                marketing partners, and other outside sources.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              In order to enhance our ability to provide relevant marketing, offers, and services to
              you and update our records, we may obtain information about you from other sources,
              such as public databases, joint marketing partners, affiliate programs, data
              providers, and from other third parties. This information includes mailing addresses,
              job titles, email addresses, phone numbers, intent data (or user behavior data),
              Internet Protocol (IP) addresses, social media profiles, social media URLs, and custom
              profiles, for purposes of targeted advertising and event promotion.
            </p>
          </section>

          {/* Browser Extension */}
          <section id="extension">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Browser Extension</h2>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> Our browser extension helps you complete online job
                applications using your Pelgo profile. It only reads and fills pages when you
                actively use it.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Pelgo offers a browser extension that helps you apply to jobs faster. When you sign
              in, the extension receives and stores a session token in your browser to authenticate
              requests to Pelgo. When you choose to autofill an application, the extension reads the
              structure and content of the application form on the page you are viewing — including
              field labels, options, and related page text — on third-party job sites and
              application portals.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              It sends this field information, together with your Pelgo profile data (such as your
              contact details, work history, and education), to Pelgo and our AI Service Providers
              to generate the values used to complete the form and to tailor and attach your resume
              and cover letter. This data is handled in accordance with this Privacy Notice,
              including how we process, share, retain, and secure your information. We do not sell
              this data or use it for advertising.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              The extension reads and fills pages only when you invoke it. It does not track your
              browsing history, monitor your activity across the web, or collect data from pages
              where you do not use it.
            </p>
          </section>

          {/* Section 2 */}
          <section id="section-2">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> We process your information to provide, improve, and
                administer our Services, communicate with you, for security and fraud prevention,
                and to comply with law. We process the personal information for the following
                purposes listed below. We may also process your information for other purposes only
                with your prior explicit consent.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>
                We process your personal information for a variety of reasons, depending on how you
                interact with our Platform, including:
              </strong>
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We process your personal information for a variety of reasons, depending on how you interact with our Platform, including:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>
                <strong>
                  To facilitate account creation and authentication and otherwise manage user
                  accounts.
                </strong>{' '}
                We may process your information so you can create and log in to your account, as
                well as keep your account in working order.
              </li>
              <li>
                <strong>To deliver and facilitate delivery of services to the user.</strong> We may
                process your information to provide you with the requested service.
              </li>
              <li>
                <strong>To respond to user inquiries/offer support to users.</strong> We may process
                your information to respond to your inquiries and solve any potential issues you
                might have with the requested service.
              </li>
              <li>
                <strong>To send administrative information to you.</strong> We may process your
                information to send you details about our products and services, changes to our
                terms and policies, and other similar information.
              </li>
              <li>
                <strong>To enable user-to-user communications.</strong> We may process your
                information if you choose to use any of our offerings that allow for communication
                with another user.
              </li>
              <li>
                <strong>To request feedback.</strong> We may process your information when necessary
                to request feedback and to contact you about your use of our Platform.
              </li>
              <li>
                <strong>To save or protect an individual's vital interest.</strong> We may process
                your information when necessary to save or protect an individual's vital interest,
                such as to prevent harm.
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="section-3">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em
                dangerouslySetInnerHTML={{
                  __html:
                    'In Short: We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect your rights, or to fulfill our legitimate business interests.',
                }}
              />
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'If you are located in the EU or UK, this section applies to you.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal bases we rely on in order to process your personal information. As such, we may rely on the following legal bases to process your personal information:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Consent. We may process your information if you have given us permission (i.e., consent) to use your personal information for a specific purpose. You can withdraw your consent at any time. Learn more about withdrawing your consent.',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Performance of a Contract. We may process your personal information when we believe it is necessary to fulfill our contractual obligations to you, including providing our Services or at your request prior to entering into a contract with you.',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Legitimate Interests. We may process your information when we believe it is reasonably necessary to achieve our legitimate business interests and those interests do not outweigh your interests and fundamental rights and freedoms. For example, we may process your personal information for some of the purposes described in order to:',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Understand how our users use our products and services so we can improve user experience',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Legal Obligations. We may process your information where we believe it is necessary for compliance with our legal obligations, such as to cooperate with a law enforcement body or regulatory agency, exercise or defend our legal rights, or disclose your information as evidence in litigation in which we are involved.',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Vital Interests. We may process your information where we believe it is necessary to protect your vital interests or the vital interests of a third party, such as situations involving potential threats to the safety of any person.',
                }}
              />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'If you are located in Canada, this section applies to you.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We may process your information if you have given us specific permission (i.e., express consent) to use your personal information for a specific purpose, or in situations where your permission can be inferred (i.e., implied consent). You can withdraw your consent at any time.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'In some exceptional cases, we may be legally permitted under applicable law to process your information without your consent, including, for example:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If collection is clearly in the interests of an individual and consent cannot be obtained in a timely way',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'For investigations and fraud detection and prevention',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'For business transactions provided certain conditions are met',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If it is contained in a witness statement and the collection is necessary to assess, process, or settle an insurance claim',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'For identifying injured, ill, or deceased persons and communicating with next of kin',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If it is reasonable to expect collection and use with consent would compromise the availability or the accuracy of the information and the collection is reasonable for purposes related to investigating a breach of an agreement or a contravention of the laws of Canada or a province',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court relating to the production of records',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If it was produced by an individual in the course of their employment, business, or profession and the collection is consistent with the purposes for which the information was produced',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If the collection is solely for journalistic, artistic, or literary purposes',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'If the information is publicly available and is specified by the regulations',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'We may disclose de-identified information for approved research or statistics projects, subject to ethics oversight and confidentiality commitments',
                }}
              />
            </ul>
          </section>

          {/* Section 4 */}
          <section id="section-4">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em
                dangerouslySetInnerHTML={{
                  __html:
                    'In Short: We may share information in specific situations described in this section and/or with the following categories of third parties.',
                }}
              />
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `Vendors, Consultants, and Other Third-Party Service Providers. We may share your data with third-party vendors, service providers, contractors, or agents ("third parties") who perform services for us or on our behalf and require access to such information to do that work.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'The categories of third parties we may share personal information with are as follows:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li dangerouslySetInnerHTML={{ __html: 'AI Platforms' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Cloud Computing Services' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Communication & Collaboration Tools' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Data Analytics Services' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Data Storage Service Providers' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Performance Monitoring Tools' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Product Engineering & Design Tools' }} />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'User Account Registration & Authentication Services',
                }}
              />
              <li dangerouslySetInnerHTML={{ __html: 'Website Hosting Service Providers' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Testing Tools' }} />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We also may need to share your personal information in the following situations:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Business Transfers. We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Affiliates. We may share your information with our affiliates, in which case we will require those affiliates to honor this Privacy Notice. Affiliates include our parent company and any subsidiaries, joint venture partners, or other companies that we control or that are under common control with us.',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Business Partners. We may share your information with our business partners to offer you certain products, services, or promotions.',
                }}
              />
            </ul>
          </section>

          {/* Section 5 */}
          <section id="section-5">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> We may use cookies and other tracking technologies to
                collect and store your information.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We may use cookies and similar tracking technologies (like web beacons and pixels) to
              gather information when you interact with our Platform. Some online tracking
              technologies help us maintain the security of our Platform and your account, prevent
              crashes, fix bugs, save your preferences, and assist with basic site functions.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We also permit third parties and service providers to use online tracking technologies
              on our Platform for analytics and advertising, including to help manage and display
              advertisements, to tailor advertisements to your interests, or to send abandoned
              shopping cart reminders (depending on your communication preferences). The third
              parties and service providers use their technology to provide advertising about
              products and services tailored to your interests which may appear either on our
              Platform or on other websites.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We do not sell personal information. For purposes of this Privacy Notice,
              &quot;sell&quot; means the disclosure of personal information to a third party in
              exchange for money or other valuable consideration.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We do not share personal information with third parties for cross-contextual
              behavioral advertising or targeted advertising.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We do not share personal information with third parties for such third parties&apos;
              direct marketing purposes.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              To the extent these online tracking technologies are deemed to be a
              &quot;sale&quot;/&quot;sharing&quot; (which includes targeted advertising, as defined
              under the applicable laws) under applicable US state laws, you can opt out of these
              online tracking technologies by submitting a request as described below under section{' '}
              <a href="#uslaws" className="text-blue-600 hover:underline">
                12. Do United States residents have specific privacy rights?
              </a>
              .
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Specific information about how we use such technologies and how you can refuse certain
              cookies is set out in our Cookie Notice.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Google Analytics</h3>
            <p className="text-slate-700 leading-relaxed">
              We may share your information with Google Analytics to track and analyze the use of
              the Platform.
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>
                Please see Google&apos;s policies at{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  https://policies.google.com/privacy
                </a>{' '}
                and{' '}
                <a
                  href="https://policies.google.com/technologies/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  https://policies.google.com/technologies/ads
                </a>
                .
              </li>
              <li>
                If you do not wish for Google Analytics to gather information about how you interact
                with our website, please install the{' '}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Google Analytics Opt-out Browser Add-on
                </a>{' '}
                and employ it at the start of each session on our website. Please note that the next
                time you access our website the analytics will be reactivated, so you will have to
                disable the technology each time.
              </li>
              <li>
                You have the option to manage Google&apos;s personalized ads by visiting{' '}
                <a
                  href="https://myadcenter.google.com/?ref=privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  https://myadcenter.google.com
                </a>
                .
              </li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To make choices about targeted advertisements from participating third parties, please
              visit the{' '}
              <a
                href="https://www.networkadvertising.org/choices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Network Advertising Initiative
              </a>{' '}
              or the{' '}
              <a
                href="https://www.aboutads.info/choices"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Digital Advertising Alliance
              </a>
              .
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              For more information about cookies, including how to manage and delete cookies, please
              visit{' '}
              <a
                href="https://www.allaboutcookies.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://www.allaboutcookies.org
              </a>
              .
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Platform. Some online tracking technologies help us maintain the security of our Platform and your account, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We also permit third parties and service providers to use online tracking technologies on our Platform for analytics and advertising, including to help manage and display advertisements, to tailor advertisements to your interests, or to send abandoned shopping cart reminders (depending on your communication preferences). The third parties and service providers use their technology to provide advertising about products and services tailored to your interests which may appear either on our Platform or on other websites.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We do not sell personal information. For purposes of this Privacy Notice, “sell” means the disclosure of personal information to a third party in exchange for money or other valuable consideration.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We do not share personal information with third parties for cross-contextual behavioral advertising or targeted advertising.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We do not share personal information with third parties for such third parties’ direct marketing purposes.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `To the extent these online tracking technologies are deemed to be a "sale"/"sharing" (which includes targeted advertising, as defined under the applicable laws) under applicable US state laws, you can opt out of these online tracking technologies by submitting a request as described below under section DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Notice.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Google Analytics' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We may share your information with Google Analytics to track and analyze the use of the Platform.',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html: `Please see Google’s policies here:  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://policies.google.com/privacy</a> and <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://policies.google.com/technologies/ads</a>`,
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: `If you do not wish for Google Analytics to gather information about how you interact with our website, please install the Google Analytics Opt-out Browser Add-on available here: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://tools.google.com/dlpage/gaoptout</a> and employ it at the start of each session on our website. A cookie will be set on your browser that instructs this technology not to start for that session. Please note that the next time you access our website the analytics will be reactivated, so you will have to disable the technology each time.`,
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: `You have the option to manage Google's personalized ads by visiting the Google advertising center at <a href="https://myadcenter.google.com/?ref=privacy-policy." target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://myadcenter.google.com/?ref=privacy-policy.</a>`,
                }}
              />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `To make choices about targeted advertisements from participating third parties, including to opt-out of receiving targeted advertisements from participating third parties, please visit the Network Advertising Initiative at <a href="http://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">http://www.networkadvertising.org/choices/</a> or the Digital Advertising Alliance at www.aboutads.info/choices.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `To find out more information about cookies, including information about how to manage and delete cookies, please visit <a href="http://www.allaboutcookies.org/." target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">http://www.allaboutcookies.org/.</a>`,
              }}
            />
          </section>

          {/* Section 6 */}
          <section id="section-6">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              6. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> We offer products, features, or tools powered by
                artificial intelligence, machine learning, or similar technologies.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              As part of our Services, we offer products, features, or tools powered by artificial
              intelligence, machine learning, or similar technologies (collectively, "AI Products").
              These tools are designed to enhance your experience and provide you with innovative
              solutions. The terms in this Privacy Notice govern your use of the AI Products within
              our Platform.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Use of AI Technologies</strong>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We provide the AI Products through third-party service providers ("AI Service
              Providers"), including Amazon Web Services (AWS) AI, Amazon Bedrock, Google Cloud AI,
              Microsoft Azure AI, Anthropic and OpenAI. As outlined in this Privacy Notice, your
              input, output, and personal information will be shared with and processed by these AI
              Service Providers to enable your use of our AI Products for purposes outlined in{' '}
              <a href="#legalbases" className="text-blue-600 hover:underline">
                WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
              </a>
              . You must not use the AI Products in any way that violates the terms or policies of
              any AI Service Provider.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Our AI Products</strong>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Our AI Products are designed for the following functions:
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `As part of our Services, we offer products, features, or tools powered by artificial intelligence, machine learning, or similar technologies (collectively, "AI Products"). These tools are designed to enhance your experience and provide you with innovative solutions. The terms in this Privacy Notice govern your use of the AI Products within our Platform.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Use of AI Technologies' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `We provide the AI Products through third-party service providers ("AI Service Providers"), including Amazon Web Services (AWS) AI, Amazon Bedrock, Google Cloud AI, Microsoft Azure AI, Anthropic and OpenAI. As outlined in this Privacy Notice, your input, output, and personal information will be shared with and processed by these AI Service Providers to enable your use of our AI Products for purposes outlined in WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?. You must not use the AI Products in any way that violates the terms or policies of any AI Service Provider.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Our AI Products' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'Our AI Products are designed for the following functions:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li dangerouslySetInnerHTML={{ __html: 'AI bots' }} />
              <li dangerouslySetInnerHTML={{ __html: 'AI applications' }} />
              <li dangerouslySetInnerHTML={{ __html: 'AI document generation' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Text analysis' }} />
              <li dangerouslySetInnerHTML={{ __html: 'AI development' }} />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'How We Process Your Data Using AI' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `All personal information processed using our AI Products is handled in line with our Privacy Notice and our agreement with third parties. This ensures high security and safeguards your personal information throughout the process, giving you peace of mind about your data's safety.`,
              }}
            />
          </section>

          {/* Section 7 */}
          <section id="section-7">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              7. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em
                dangerouslySetInnerHTML={{
                  __html:
                    'In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.',
                }}
              />
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than the period of time in which users have an account with us.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.',
              }}
            />
          </section>

          {/* Section 8 */}
          <section id="section-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              8. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> We aim to protect your personal information through a
                system of organizational and technical security measures.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We have implemented appropriate and reasonable technical and organizational security
              measures designed to protect the security of any personal information we process.
              However, despite our safeguards and efforts to secure your information, no electronic
              transmission over the Internet or information storage technology can be guaranteed to
              be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or
              other unauthorized third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information. Although we will do our
              best to protect your personal information, transmission of personal information to and
              from our Platform is at your own risk. You should only access the Platform within a
              secure environment.
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Platform is at your own risk. You should only access the Platform within a secure environment.',
              }}
            />
          </section>

          {/* Section 9 */}
          <section id="section-9">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              9. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> We do not knowingly collect data from or market to
                children under 18 years of age.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We do not knowingly collect, solicit data from, or market to children under 18 years
              of age, nor do we knowingly sell such personal information. By using the Platform, you
              represent that you are at least 18 or that you are the parent or guardian of such a
              minor and consent to such minor dependent&apos;s use of the Platform. If we learn that
              personal information from users less than 18 years of age has been collected, we will
              deactivate the account and take reasonable measures to promptly delete such data from
              our records. If you become aware of any data we may have collected from children under
              age 18, please contact us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              .
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we knowingly sell such personal information. By using the Platform, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Platform. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">legal@pelgo.com</a>.`,
              }}
            />
          </section>

          {/* Section 10 */}
          <section id="section-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              10. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em
                dangerouslySetInnerHTML={{
                  __html:
                    'In Short: Depending on your state of residence in the US or in some regions, such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.',
                }}
              />
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable data protection laws. These may include the right (i) to request access and obtain a copy of your personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of your personal information; (iv) if applicable, to data portability; and (v) not to be subject to automated decision-making. If a decision that produces legal or similarly significant effects is made solely by automated means, we will inform you, explain the main factors, and offer a simple way to request human review. In certain circumstances, you may also have the right to object to the processing of your personal information. You can make such a request by contacting us by using the contact details provided in the section HOW CAN YOU CONTACT US ABOUT THIS NOTICE? below.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We will consider and act upon any request in accordance with applicable data protection laws.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'If you are located in the EEA or UK and you believe we are unlawfully processing your personal information, you also have the right to complain to your Member State data protection authority or UK data protection authority.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'If you are located in Switzerland, you may contact the Federal Data Protection and Information Commissioner.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Withdrawing your consent: If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section HOW CAN YOU CONTACT US ABOUT THIS NOTICE? below.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `Opting out of marketing and promotional communications: You can unsubscribe from our marketing and promotional communications at any time by clicking on the unsubscribe link in the emails that we send, replying "STOP" or "UNSUBSCRIBE" to the SMS messages that we send, or by contacting us using the details provided in the section HOW CAN YOU CONTACT US ABOUT THIS NOTICE? below. You will then be removed from the marketing lists. However, we may still communicate with you — for example, to send you service-related messages that are necessary for the administration and use of your account, to respond to service requests, or for other non-marketing purposes.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Information sharing to subcontractors in support services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with third parties.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Account Information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'If you would at any time like to review or change the information in your account or terminate your account, you can:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Contact us using the contact information provided.',
                }}
              />
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Upon your request to terminate your account, we will deactivate or delete your account
              and information from our active databases. However, we may retain some information in
              our files to prevent fraud, troubleshoot problems, assist with any investigations,
              enforce our legal terms and/or comply with applicable legal requirements.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>
                <u>Cookies and similar technologies:</u>
              </strong>{' '}
              Most Web browsers are set to accept cookies by default. If you prefer, you can usually
              choose to set your browser to remove cookies and to reject cookies. If you choose to
              remove cookies or reject cookies, this could affect certain features or services of
              our Platform.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              The Right to Lodge a Complaint with a Supervisory Authority
            </h3>
            <p className="text-slate-700 leading-relaxed">
              Subject to applicable privacy law, should you wish to report a complaint or if you
              feel that we have not addressed your concern in a satisfactory manner, you have the
              right to lodge a complaint with your local data protection authority and/or competent
              supervisory authority. If you work or reside in a country that is a member of the
              European Union or that is in the European Economic Area, you may find the contact
              details for your appropriate data protection authority at{' '}
              <a
                href="https://edpb.europa.eu/about-edpb/about-edpb/members_en"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://edpb.europa.eu/about-edpb/about-edpb/members_en
              </a>
              . If you are a resident of the United Kingdom you may contact the UK supervisory
              authority, the Information Commissioner&apos;s Office, at{' '}
              <a
                href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/
              </a>
              . If you are a resident of Texas, you may lodge a complaint online at{' '}
              <a
                href="https://consumerprotection.texasattorneygeneral.gov/consumercomplaintportal/s/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://consumerprotection.texasattorneygeneral.gov/consumercomplaintportal/s/
              </a>
              .
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              If you have questions or comments about your privacy rights, you may email us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              .
            </p>
          </section>

          {/* Section 11 */}
          <section id="section-11">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              11. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Notice.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'California law requires us to let you know how we respond to web browser DNT signals. Because there currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at this time.',
              }}
            />
          </section>

          {/* Section 12 */}
          <section id="section-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em
                dangerouslySetInnerHTML={{
                  __html:
                    'In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. More information is provided below.',
                }}
              />
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Categories of Personal Information We Collect' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'The table below shows the categories of personal information we have collected in the past twelve (12) months. The table includes illustrative examples of each category and does not reflect the personal information we collect from you. For a comprehensive inventory of all personal information we process, please refer to the section WHAT INFORMATION DO WE COLLECT?.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Category' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Examples' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Collected' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'A. Identifiers' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'B. Personal information as defined in the California Customer Records statute',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Name, contact information, education, employment, employment history, and financial information',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'NO' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'C. Protected classification characteristics under state or federal law',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'D. Commercial information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Transaction information, purchase history, financial details, and payment information',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'E. Biometric information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Fingerprints and voiceprints' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'NO' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'F. Internet or other similar network activity' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'G. Geolocation data' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Device location' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'NO' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'H. Audio, electronic, sensory, or similar information',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Images and audio, video or call recordings created in connection with our business activities',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'I. Professional or employment-related information',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'J. Education Information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Student records and directory information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'K. Inferences drawn from collected personal information',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'YES' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'L. Sensitive personal Information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'NO' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We may also collect other personal information outside of these categories through instances where you interact with us in person, online, or by phone or mail in the context of:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Receiving help through our customer support channels;',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Participation in customer surveys or contests; and',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Facilitation in the delivery of our Services and to respond to your inquiries.',
                }}
              />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We will use and retain the collected personal information as needed to provide the Services or for:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category A - As long as the user has an account with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category C - As long as the user has an account with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category D - As long as the user has an account with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category F - As long as the user has an account with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category H - As long as the user has an account with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category I - As long as the user has an account with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category J - As long as the user has an account with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Category K - As long as the user has an account with us',
                }}
              />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Sources of Personal Information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Learn more about the sources of personal information we collect in WHAT INFORMATION DO WE COLLECT?.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'How We Use and Share Personal Information' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Learn more about how we use your personal information in the section, HOW DO WE PROCESS YOUR INFORMATION?.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'Will your information be shared with anyone else?',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We may disclose your personal information with our service providers pursuant to a written contract between us and each service provider. Learn more about how we disclose personal information to in the section, WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be "selling" of your personal information.`,
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We have disclosed the following categories of personal information to third parties for a business or commercial purpose in the preceding twelve (12) months:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li dangerouslySetInnerHTML={{ __html: 'Category A' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Category C' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Category D' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Category F' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Category H' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Category I' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Category J' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Category K' }} />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'The categories of third parties to whom we disclosed personal information for a business or commercial purpose can be found under WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?.',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'We have sold or shared the following categories of personal information to third parties in the preceding twelve (12) months:',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: 'The categories of third parties to whom we sold personal information are:',
              }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'The categories of third parties to whom we shared personal information with are:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li dangerouslySetInnerHTML={{ __html: 'AI Platforms' }} />
              <li dangerouslySetInnerHTML={{ __html: 'Data Analytics Services' }} />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'User Account Registration & Authentication Services',
                }}
              />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{ __html: 'Your Rights' }}
            />
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Right to know whether or not we are processing your personal data',
                }}
              />
              <li dangerouslySetInnerHTML={{ __html: 'Right to access your personal data' }} />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Right to correct inaccuracies in your personal data',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Right to request the deletion of your personal data',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to obtain a copy of the personal data you previously shared with us',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: 'Right to non-discrimination for exercising your rights',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: `Right to opt out of the processing of your personal data if it is used for targeted advertising (or sharing as defined under California's privacy law), the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects ("profiling")`,
                }}
              />
            </ul>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html:
                  'Depending upon the state where you live, you may also have the following rights:',
              }}
            />
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to access the categories of personal data being processed (as permitted by applicable law, including the privacy law in Minnesota)',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in California, Delaware, and Maryland)',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in Minnesota and Oregon)',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to obtain a list of third parties to which we have sold personal data (as permitted by applicable law, including the privacy law in Connecticut)',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to review, understand, question, and depending on where you live, correct how personal data has been profiled (as permitted by applicable law, including the privacy law in Connecticut and Minnesota)',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including the privacy law in California)',
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    'Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including the privacy law in Florida)',
                }}
              />
            </ul>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              How to Exercise Your Rights
            </h3>
            <p className="text-slate-700 leading-relaxed">
              To exercise these rights, you can contact us by visiting{' '}
              <a
                href="https://www.Pelgo.com/Privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://www.Pelgo.com/Privacy
              </a>
              , by emailing us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                Legal@Pelgo.com
              </a>
              , or by referring to the contact details at the bottom of this document.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              You can opt out from the selling of your personal information, targeted advertising,
              or profiling by disabling cookies in Cookie Preference Settings.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Under certain US state data protection laws, you can designate an authorized agent to
              make a request on your behalf. We may deny a request from an authorized agent that
              does not submit proof that they have been validly authorized to act on your behalf in
              accordance with applicable laws.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Request Verification</h3>
            <p className="text-slate-700 leading-relaxed">
              Upon receiving your request, we will need to verify your identity to determine you are
              the same person about whom we have the information in our system. We will only use
              personal information provided in your request to verify your identity or authority to
              make the request. However, if we cannot verify your identity from the information
              already maintained by us, we may request that you provide additional information for
              the purposes of verifying your identity and for security or fraud-prevention purposes.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              If you submit the request through an authorized agent, we may need to collect
              additional information to verify your identity before processing your request and the
              agent will need to provide a written and signed permission from you to submit such
              request on your behalf.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Appeals</h3>
            <p className="text-slate-700 leading-relaxed">
              Under certain US state data protection laws, if we decline to take action regarding
              your request, you may appeal our decision by emailing us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                Legal@Pelgo.com
              </a>
              . We will inform you in writing of any action taken or not taken in response to the
              appeal, including a written explanation of the reasons for the decisions. If your
              appeal is denied, you may submit a complaint to your state attorney general.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              California "Shine The Light" Law
            </h3>
            <p className="text-slate-700 leading-relaxed">
              California Civil Code Section 1798.83, also known as the "Shine The Light" law,
              permits our users who are California residents to request and obtain from us, once a
              year and free of charge, information about categories of personal information (if any)
              we disclosed to third parties for direct marketing purposes and the names and
              addresses of all third parties with which we shared personal information in the
              immediately preceding calendar year. If you are a California resident and would like
              to make such a request, please submit your request in writing to us by using the
              contact details provided in the section{' '}
              <a href="#contact" className="text-blue-600 hover:underline">
                HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
              </a>
              .
            </p>
          </section>

          {/* Section 13 */}
          <section id="section-13">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              13. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <em>
                <strong>In Short:</strong> Yes, we will update this notice as necessary to stay
                compliant with relevant laws.
              </em>
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We may update this Privacy Notice from time to time. The updated version will be
              indicated by an updated &quot;Last updated&quot; (or &quot;Revised&quot;) date at the
              top of this Privacy Notice. If we make material changes to this Privacy Notice, we may
              notify you either by prominently posting a notice of such changes or by directly
              sending you a notification. We encourage you to review this Privacy Notice frequently
              to be informed of how we are protecting your information.
            </p>
            <p
              className="text-slate-700 leading-relaxed mt-4"
              dangerouslySetInnerHTML={{
                __html: `We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.`,
              }}
            />
          </section>

          {/* Section 14 */}
          <section id="section-14">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              14. How can you contact us about this notice?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              If you have questions or comments about this notice, you may email us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>{' '}
              or contact us by post at:
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Pelgo, Inc.</strong>
              <br />
              838 Walker Rd, Suite 21-2
              <br />
              Dover, DE 19904
              <br />
              United States
            </p>
          </section>

          {/* Section 15 */}
          <section id="section-15">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              15. How can you review, update, or delete the data we collect from you?
            </h2>
            <p className="text-slate-700 leading-relaxed">
              Based on the applicable laws of your country or state of residence in the US, you may
              have the right to request access to the personal information we collect from you,
              details about how we have processed it, correct inaccuracies, or delete your personal
              information. You may also have the right to withdraw your consent to our processing of
              your personal information. These rights may be limited in some circumstances by
              applicable law. To request to review, update, or delete your personal information,
              please visit{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                this page
              </Link>{' '}
              or contact us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>

      <SiteFooter />
    </div>
  )
}
